import type { ExecutionTask, GateChecklistItem, GateTag, GateTagStatus, GateTagType } from "@/engine/types";
import { newId } from "./tree-ops";

/**
 * 검수 상태 기계.
 * 검수가 없거나 모두 통과면 작업은 종전처럼 완료할 수 있다.
 * 하나라도 열려 있으면 완료가 막히고, 전부 통과하면 완료가 되며, 통과 뒤 반려되거나 다시 열리면 진행 중으로 돌아간다.
 */

export const GATE_TYPES: GateTagType[] = ["qa", "spec", "uat", "security", "custom"];
export const GATE_STATUSES: GateTagStatus[] = ["unverified", "ready", "passed", "failed"];

export const GATE_TYPE_LABEL: Record<GateTagType, string> = {
  qa: "품질 검수",
  spec: "기획 확인",
  uat: "고객 인수",
  security: "보안 검수",
  custom: "직접 지정",
};

export const GATE_STATUS_LABEL: Record<GateTagStatus, string> = {
  unverified: "확인 전",
  ready: "검수 대기",
  passed: "통과",
  failed: "반려",
};

/** 검수 기준이 비어 있을 때 한 번에 넣는 예시 문장. */
export const BDD_TEMPLATE = "이런 상황에서는\n이렇게 하면\n이렇게 보여야 한다";

const QA_STATUSES = new Set<GateChecklistItem["qaStatus"]>(["pending", "pass", "fail", "block"]);

export function gateTagsOf(task: { gateTags?: GateTag[] }): GateTag[] {
  return task.gateTags ?? [];
}

export function makeGateTag(type: GateTagType, criteria: string[] = [], name?: string): GateTag {
  const kind: GateTagType = GATE_TYPES.includes(type) ? type : "custom";
  const texts = criteria.map((c) => c.trim()).filter(Boolean);
  return {
    id: newId("g"),
    name: name?.trim() || GATE_TYPE_LABEL[kind],
    type: kind,
    status: "unverified",
    checklist: texts.map((text) => ({ id: newId("gc"), text, devChecked: false, qaStatus: "pending" as const })),
  };
}

/** 개발자가 항목을 모두 확인하면 Ready. 하나라도 풀면 검수 대기·통과는 미검증으로 돌아간다. */
export function toggleGateChecklistDev(gate: GateTag, itemId: string): GateTag {
  if (!gate.checklist.some((c) => c.id === itemId)) return gate;
  const checklist = gate.checklist.map((c) => (c.id === itemId ? { ...c, devChecked: !c.devChecked } : c));
  const allDev = checklist.length > 0 && checklist.every((c) => c.devChecked);
  let status = gate.status;
  if (status === "unverified" && allDev) status = "ready";
  else if ((status === "ready" || status === "passed") && !allDev) status = "unverified";
  return finishGate({ ...gate, checklist, status }, gate.status);
}

/** 검수 요청. 체크가 덜 됐어도 개발자가 명시적으로 Ready로 올린다. 결함 사유는 지운다. */
export function requestGateReview(gate: GateTag): GateTag {
  if (gate.status === "passed") return gate;
  if (gate.status === "ready" && gate.failReason === undefined) return gate;
  const { failReason: _drop, ...rest } = gate;
  return { ...rest, status: "ready" };
}

/**
 * 항목 단위 QA 판정.
 * Fail·Block은 사유가 있어야 하고 게이트를 Failed로 만든다.
 * 모든 항목이 Pass이고 게이트가 이미 검수 흐름에 있으면 Passed.
 */
export function setGateChecklistQaStatus(
  gate: GateTag,
  itemId: string,
  qaStatus: GateChecklistItem["qaStatus"],
  actor: string,
  reason?: string,
  now = new Date().toISOString(),
): GateTag {
  if (!gate.checklist.some((c) => c.id === itemId)) return gate;
  const why = reason?.trim();
  if ((qaStatus === "fail" || qaStatus === "block") && !why) return gate;
  const checklist = gate.checklist.map((c) => {
    if (c.id !== itemId) return c;
    if (qaStatus === "fail" || qaStatus === "block") return { ...c, qaStatus, failReason: why };
    const { failReason: _drop, ...rest } = c;
    return { ...rest, qaStatus };
  });
  const anyFail = checklist.some((c) => c.qaStatus === "fail" || c.qaStatus === "block");
  const allPass = checklist.length > 0 && checklist.every((c) => c.qaStatus === "pass");
  const inReview = gate.status === "ready" || gate.status === "failed" || gate.status === "passed";
  let status = gate.status;
  if (anyFail) status = "failed";
  else if (allPass && inReview) status = "passed";
  else if (gate.status === "passed" || gate.status === "failed") status = "ready";
  const failed = checklist.find((c) => c.qaStatus === "fail" || c.qaStatus === "block");
  const next: GateTag = { ...gate, checklist, status };
  if (status === "failed") return { ...next, failReason: failed?.failReason, verifiedBy: actor, verifiedAt: now };
  if (status === "passed") {
    const { failReason: _drop, ...rest } = next;
    return { ...rest, verifiedBy: actor, verifiedAt: now };
  }
  return finishGate(next, gate.status);
}

/** 태그 단위 판정. Ready가 아니면 무시하고, Fail은 사유가 비어 있으면 무시한다. */
export function setGateTagVerdict(gate: GateTag, verdict: "pass" | "fail", actor: string, reason?: string, now = new Date().toISOString()): GateTag {
  if (gate.status !== "ready") return gate;
  if (verdict === "fail") {
    const why = reason?.trim();
    if (!why) return gate;
    return { ...gate, status: "failed", failReason: why, verifiedBy: actor, verifiedAt: now };
  }
  const { failReason: _drop, ...rest } = gate;
  return { ...rest, status: "passed", verifiedBy: actor, verifiedAt: now };
}

export function addGateChecklistItem(gate: GateTag, text: string): GateTag {
  const t = text.trim();
  if (!t) return gate;
  const checklist = [...gate.checklist, { id: newId("gc"), text: t, devChecked: false, qaStatus: "pending" as const }];
  const status = gate.status === "passed" || gate.status === "ready" ? "unverified" : gate.status;
  return finishGate({ ...gate, checklist, status }, gate.status);
}

export function removeGateChecklistItem(gate: GateTag, itemId: string): GateTag {
  if (!gate.checklist.some((c) => c.id === itemId)) return gate;
  const checklist = gate.checklist.filter((c) => c.id !== itemId);
  const anyFail = checklist.some((c) => c.qaStatus === "fail" || c.qaStatus === "block");
  const allPass = checklist.length > 0 && checklist.every((c) => c.qaStatus === "pass");
  const allDev = checklist.length > 0 && checklist.every((c) => c.devChecked);
  let status = gate.status;
  if (anyFail) status = "failed";
  else if (allPass && (gate.status === "ready" || gate.status === "failed" || gate.status === "passed")) status = "passed";
  else if (gate.status === "passed" || gate.status === "failed") status = allDev ? "ready" : "unverified";
  else if (gate.status === "ready" && !allDev) status = "unverified";
  else if (gate.status === "unverified" && allDev) status = "ready";
  return finishGate({ ...gate, checklist, status }, gate.status);
}

export function addGateTag(task: ExecutionTask, gate: GateTag, now = new Date().toISOString()): ExecutionTask {
  return settleTaskByGates({ ...task, gateTags: [...gateTagsOf(task), gate], updatedAt: now }, now);
}

export function removeGateTag(task: ExecutionTask, gateId: string, now = new Date().toISOString()): ExecutionTask {
  const gateTags = gateTagsOf(task).filter((g) => g.id !== gateId);
  const next: ExecutionTask = { ...task, updatedAt: now };
  if (gateTags.length) next.gateTags = gateTags;
  else delete next.gateTags;
  return settleTaskByGates(next, now);
}

export function updateGateTag(task: ExecutionTask, gateId: string, fn: (gate: GateTag) => GateTag, now = new Date().toISOString()): ExecutionTask {
  const tags = gateTagsOf(task);
  if (!tags.some((g) => g.id === gateId)) return task;
  return settleTaskByGates({ ...task, gateTags: tags.map((g) => (g.id === gateId ? fn(g) : g)), updatedAt: now }, now);
}

/** 게이트가 전부 통과하면 Done, Done인 태스크에 열린 게이트가 생기면 진행 중으로 되돌린다. */
export function settleTaskByGates(task: ExecutionTask, now = new Date().toISOString()): ExecutionTask {
  const tags = gateTagsOf(task);
  if (tags.length > 0 && tags.every((g) => g.status === "passed")) {
    return { ...task, gateTags: tags, status: "done", completedAt: task.completedAt ?? now };
  }
  if (task.status === "done" && tags.some((g) => g.status !== "passed")) {
    const { completedAt: _drop, ...rest } = task;
    return { ...rest, ...(tags.length ? { gateTags: tags } : {}), status: "inProgress" };
  }
  return task;
}

export function canCompleteExecutionTask(task: ExecutionTask): { allowed: boolean; blockingGates: GateTag[] } {
  const blockingGates = gateTagsOf(task).filter((g) => g.status !== "passed");
  return { allowed: blockingGates.length === 0, blockingGates };
}

export function describeGateBlock(gates: GateTag[]): string {
  const names = gates.map((g) => g.name).filter(Boolean);
  if (!names.length) return "완료하려면 검수를 통과해야 합니다.";
  return `완료하려면 ${names.join(", ")} 통과가 필요합니다.`;
}

/** 복사·반복 발생은 통과 이력을 물려받지 않는다. */
export function resetGateTags(tags: GateTag[] | undefined): GateTag[] {
  return (tags ?? []).map((g) => ({
    id: newId("g"),
    name: g.name,
    type: g.type,
    status: "unverified" as const,
    ...(g.requiredRole ? { requiredRole: g.requiredRole } : {}),
    checklist: g.checklist.map((c) => ({ id: newId("gc"), text: c.text, devChecked: false, qaStatus: "pending" as const })),
  }));
}

export interface GateRollup {
  gateTotal: number;
  gatePassed: number;
  gateFailed: number;
  gateReady: number;
  /** 아직 통과하지 않은 고객 인수 검수 수 */
  uatPending: number;
}

export function rollupGates(tasks: readonly ExecutionTask[]): GateRollup {
  const rollup: GateRollup = { gateTotal: 0, gatePassed: 0, gateFailed: 0, gateReady: 0, uatPending: 0 };
  for (const task of tasks) {
    for (const gate of gateTagsOf(task)) {
      rollup.gateTotal += 1;
      if (gate.status === "passed") rollup.gatePassed += 1;
      else if (gate.status === "failed") rollup.gateFailed += 1;
      else if (gate.status === "ready") rollup.gateReady += 1;
      if (gate.type === "uat" && gate.status !== "passed") rollup.uatPending += 1;
    }
  }
  return rollup;
}

export function normalizeGateTags(raw: unknown): GateTag[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: GateTag[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const id = typeof r.id === "string" && r.id ? r.id : newId("g");
    if (seen.has(id)) continue;
    seen.add(id);
    const type: GateTagType = GATE_TYPES.includes(r.type as GateTagType) ? (r.type as GateTagType) : "custom";
    const status: GateTagStatus = GATE_STATUSES.includes(r.status as GateTagStatus) ? (r.status as GateTagStatus) : "unverified";
    const checklist = normalizeChecklist(r.checklist);
    const gate: GateTag = {
      id,
      name: typeof r.name === "string" && r.name.trim() ? r.name.trim() : GATE_TYPE_LABEL[type],
      type,
      status,
      checklist,
    };
    if (typeof r.requiredRole === "string" && r.requiredRole) gate.requiredRole = r.requiredRole;
    if (typeof r.failReason === "string" && r.failReason) gate.failReason = r.failReason;
    if (typeof r.verifiedBy === "string" && r.verifiedBy) gate.verifiedBy = r.verifiedBy;
    if (typeof r.verifiedAt === "string" && r.verifiedAt) gate.verifiedAt = r.verifiedAt;
    out.push(gate);
  }
  return out;
}

function normalizeChecklist(raw: unknown): GateChecklistItem[] {
  if (!Array.isArray(raw)) return [];
  const out: GateChecklistItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    if (typeof r.text !== "string" || !r.text.trim()) continue;
    const qaStatus: GateChecklistItem["qaStatus"] = QA_STATUSES.has(r.qaStatus as GateChecklistItem["qaStatus"])
      ? (r.qaStatus as GateChecklistItem["qaStatus"])
      : "pending";
    const row: GateChecklistItem = {
      id: typeof r.id === "string" && r.id ? r.id : newId("gc"),
      text: r.text.trim(),
      devChecked: r.devChecked === true,
      qaStatus,
    };
    if (typeof r.failReason === "string" && r.failReason) row.failReason = r.failReason;
    out.push(row);
  }
  return out;
}

/** Passed·Failed가 아닌 상태로 나가면 결함 사유를 지운다. */
function finishGate(gate: GateTag, prev: GateTagStatus): GateTag {
  if (gate.status === "passed" || gate.status === "failed") return gate;
  if (gate.failReason === undefined && (prev === gate.status || prev === "unverified" || prev === "ready")) return gate;
  const { failReason: _drop, ...rest } = gate;
  return rest;
}
